import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Input,
  Icon,
  message,
  Slider,
  Tooltip,
  Button,
  Progress,
  Spin
} from 'antd'
import { config } from '../../../../../constants'
import { T, Label, Chip, fmtSec } from './AudioEditor/SharedLayout'

const { TextArea } = Input
const AUDIO_PREVIEW_BASE_URL = config.videoGenerationUrl + 'audio/'

const VoiceoverModal = ({
  visible,
  scene,
  voices = [],
  selectedVoice: globalSelectedVoice,
  token,
  onClose,
  formData,
  onGenerate,
  onNavigateToStep,
  isClusteringAudio,
  audioClusteringProgress = 0,
  audioClusteringStatus = '',
  audioMode,
  onStopGeneration
}) => {
  const [voiceText, setVoiceText] = useState('')
  const [speedFactor, setSpeedFactor] = useState(0.7)
  const [exaggeration, setExaggeration] = useState(0.5)
  const [cfgWeight, setCfgWeight] = useState(0.8)
  const [customPrompt, setCustomPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showStopConfirm, setShowStopConfirm] = useState(false)
  const [previewPlaying, setPreviewPlaying] = useState(null)
  const previewRef = useRef(null)

  const sceneKey = scene?.id
  const isEasyAudioMode = audioMode === 'narration'
  const hasAudio = Boolean(scene?.audio_link)
  const charCount = voiceText?.length || 0
  const overLimit = charCount > 5000

  useEffect(() => {
    if (visible && scene && !voiceText) {
      const d = scene.aggregated_voiceover
      setVoiceText(typeof d === 'string' ? d : d?.text || '')
    }
  }, [visible])

  useEffect(() => {
    return () => {
      previewRef.current && previewRef.current.pause()
    }
  }, [])

  const handleGenerate = async () => {
    if (!globalSelectedVoice) {
      message.warning('Select a voice first')
      return
    }
    if (!(voiceText && voiceText.trim())) {
      message.warning('Enter voiceover text')
      return
    }
    setIsGenerating(true)
    try {
      await onGenerate({
        voice_id: globalSelectedVoice,
        text: voiceText,
        scene_structure: formData?.shotList,
        scene_key: sceneKey,
        speed_factor: speedFactor,
        exaggeration,
        cfg_weight: cfgWeight,
        prompt: customPrompt
      })
      message.success('Voiceover generated')
    } catch (e) {
      message.error(e?.message || 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  const togglePreview = id => {
    if (previewPlaying === id) {
      previewRef.current && previewRef.current.pause()
      previewRef.current = null
      setPreviewPlaying(null)
    } else {
      previewRef.current && previewRef.current.pause()
      const a = new Audio(`https://video-generate.sharelookapp.link/audio/${id}`)
      a.play().catch(() => message.error('Preview failed'))
      a.onended = () => setPreviewPlaying(null)
      previewRef.current = a
      setPreviewPlaying(id)
    }
  }

  const voiceName = id => {
    if (!id) return null
    const v = voices.find(v => v?.id === id || v?.voice_id === id || v === id)
    return v?.name || v?.label || id
  }

  // ── Clustering overlay ────────────────────────────────────────
  if (isClusteringAudio) {
    return (
      <Modal
        visible={visible}
        footer={null}
        width={440}
        maskClosable={false}
        onCancel={onClose}
        centered
        bodyStyle={{
          padding: '44px 36px',
          textAlign: 'center',
          fontFamily: T.font
        }}
      >
        <Spin size="large" />
        <div
          style={{ marginTop: 20, fontSize: 16, fontWeight: 600, color: T.ink }}
        >
          Generating Audio
        </div>
        <div
          style={{
            color: T.inkLight,
            marginTop: 4,
            marginBottom: 24,
            fontSize: 13
          }}
        >
          {isEasyAudioMode
            ? 'Building narration track'
            : 'Rebuilding narration and dialogue tracks'}
        </div>
        <Progress
          percent={audioClusteringProgress}
          status={audioClusteringStatus === 'failed' ? 'exception' : 'active'}
          strokeColor={T.blue}
          style={{ maxWidth: 300, margin: '0 auto' }}
        />
        {audioClusteringStatus && (
          <div style={{ marginTop: 8, fontSize: 12, color: T.inkLight }}>
            {audioClusteringStatus}
          </div>
        )}
        <div style={{ marginTop: 32 }}>
          <Button
            type="danger"
            ghost
            size="small"
            style={{ fontSize: 12, borderRadius: 20 }}
            onClick={() => setShowStopConfirm(true)}
          >
            Stop Generation
          </Button>
        </div>

        {/* Custom Confirmation Modal */}
        <Modal
          visible={showStopConfirm}
          onCancel={() => setShowStopConfirm(false)}
          footer={null}
          width={400}
          centered
          bodyStyle={{ padding: '32px 28px', textAlign: 'center' }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#FFF1F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 8
              }}
            >
              <Icon
                type="exclamation-circle"
                style={{ color: '#FF4D4F', fontSize: 24 }}
              />
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: T.ink,
                marginBottom: 4
              }}
            >
              Stop Audio Generation?
            </div>

            <div
              style={{
                fontSize: 14,
                color: T.inkLight,
                lineHeight: 1.5,
                marginBottom: 24
              }}
            >
              Are you sure you want to stop this audio generation?
              <br />
              <span style={{ color: '#FF4D4F', fontWeight: 600 }}>
                Important: Generation cost will not be refunded for this closed
                request.
              </span>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                width: '100%',
                justifyContent: 'center'
              }}
            >
              <Button
                onClick={() => setShowStopConfirm(false)}
                style={{
                  flex: 1,
                  height: 40,
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Continue
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  onStopGeneration && onStopGeneration()
                  setShowStopConfirm(false)
                  onClose()
                }}
                style={{
                  flex: 1,
                  height: 40,
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  background: '#FF4D4F',
                  borderColor: '#FF4D4F'
                }}
              >
                Stop
              </Button>
            </div>
          </div>
        </Modal>
      </Modal>
    )
  }

  // ── Main modal ────────────────────────────────────────────────
  return (
    <Modal
      visible={visible}
      onCancel={isGenerating ? undefined : onClose}
      footer={null}
      width={700}
      maskClosable={!isGenerating}
      destroyOnClose
      centered
      bodyStyle={{ padding: 0, fontFamily: T.font }}
      style={{ fontFamily: T.font }}
    >
      {/* Header */}
      <div
        style={{
          padding: '20px 28px 16px',
          borderBottom: `1px solid ${T.border}`
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.ink,
                letterSpacing: '-0.2px'
              }}
            >
              Voiceover Studio
            </div>
            <div style={{ fontSize: 12, color: T.inkLight, marginTop: 2 }}>
              {scene?.title || 'Scene'} ·{' '}
              {isEasyAudioMode ? 'narration' : 'dialogue'}
            </div>
          </div>
          {hasAudio && (
            <Chip color={T.green} bg={T.greenLight} border={T.greenBorder}>
              ✓ Audio ready
            </Chip>
          )}
        </div>
      </div>

      {/* Body */}
      <div
        style={{
          padding: '24px 28px 8px',
          minHeight: 320,
          maxHeight: 540,
          overflowY: 'auto'
        }}
      >
        {/* Generated audio player */}
        {hasAudio && !isGenerating && (
          <div style={{ marginBottom: 24 }}>
            <Label style={{ display: 'block', marginBottom: 8 }}>
              Generated Voiceover
            </Label>
            <audio
              controls
              src={scene.audio_link}
              style={{ width: '100%', height: 40, outline: 'none' }}
            />
          </div>
        )}

        {/* No voice warning */}
        {!globalSelectedVoice && (
          <div
            style={{
              marginBottom: 20,
              padding: '12px 16px',
              background: T.redLight,
              border: `1px solid ${T.redBorder}`,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10
            }}
          >
            <Icon
              type="exclamation-circle"
              style={{
                color: T.red,
                fontSize: 14,
                marginTop: 2,
                flexShrink: 0
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                No voice selected
              </div>
              <div style={{ fontSize: 12, color: T.inkSub, marginTop: 2 }}>
                Go to the Voiceover step to choose a voice.
              </div>
            </div>
            <Button
              size="small"
              onClick={() => {
                onClose()
                onNavigateToStep && onNavigateToStep('voiceover')
              }}
              style={{ fontSize: 12, flexShrink: 0 }}
            >
              Select voice
            </Button>
          </div>
        )}

        {/* Voice chip */}
        {globalSelectedVoice && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              marginBottom: 20,
              background: T.blueLight,
              border: `1px solid ${T.blueBorder}`,
              borderRadius: 8
            }}
          >
            <Icon
              type="sound"
              style={{ color: T.blue, fontSize: 15, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: T.blue,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Voice
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                {voiceName(globalSelectedVoice)}
              </div>
            </div>
            <button
              onClick={() => togglePreview(globalSelectedVoice)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color:
                  previewPlaying === globalSelectedVoice ? T.blue : T.inkLight,
                fontSize: 18,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Icon
                type={
                  previewPlaying === globalSelectedVoice
                    ? 'pause-circle'
                    : 'play-circle'
                }
              />
            </button>
          </div>
        )}

        {/* Script */}
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 8
            }}
          >
            <Label>Narration Text</Label>
            <span
              style={{
                fontSize: 11,
                color: overLimit ? T.red : T.inkLight,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {charCount.toLocaleString()} / 5,000
            </span>
          </div>
          <TextArea
            rows={7}
            value={voiceText}
            onChange={e => setVoiceText(e.target.value)}
            placeholder="Enter the voiceover script…"
            disabled={isGenerating || !globalSelectedVoice}
            style={{
              fontSize: 13,
              lineHeight: 1.65,
              borderColor: overLimit ? T.red : T.border,
              borderRadius: 8,
              resize: 'none',
              fontFamily: T.font,
              color: T.ink
            }}
          />
          {overLimit && (
            <div style={{ fontSize: 12, color: T.red, marginTop: 4 }}>
              Exceeds 5,000 character limit
            </div>
          )}
        </div>

        {/* Custom Prompt */}
        <div style={{ marginBottom: 20 }}>
          <Label>Custom Prompt (Optional)</Label>
          <TextArea
            rows={2}
            value={customPrompt}
            onChange={e => setCustomPrompt(e.target.value)}
            placeholder="Add any extra instructions for voice tone, emotion, or style…"
            disabled={isGenerating || !globalSelectedVoice}
            style={{
              fontSize: 13,
              marginTop: 8,
              borderColor: T.border,
              borderRadius: 8,
              resize: 'none',
              fontFamily: T.font,
              color: T.ink
            }}
          />
        </div>

        {/* Advanced settings */}
        <button
          onClick={() => {
            if (!isGenerating && globalSelectedVoice) setShowAdvanced(p => !p)
          }}
          disabled={isGenerating || !globalSelectedVoice}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontSize: 12,
            color: T.blue,
            fontWeight: 500,
            fontFamily: T.font,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            opacity: isGenerating || !globalSelectedVoice ? 0.4 : 1
          }}
        >
          <Icon type={showAdvanced ? 'up' : 'down'} style={{ fontSize: 10 }} />
          Advanced settings
        </button>

        {showAdvanced && (
          <div
            style={{
              marginTop: 14,
              padding: '16px 18px 4px',
              background: T.surface,
              borderRadius: 8,
              border: `1px solid ${T.border}`
            }}
          >
            {[
              {
                key: 's',
                label: 'Speed',
                tip: '0.5× = half, 2.0× = double',
                val: speedFactor,
                set: setSpeedFactor,
                min: 0.5,
                max: 2.0,
                step: 0.1,
                marks: { 0.5: '0.5×', 1.0: '1×', 2.0: '2×' }
              },
              {
                key: 'e',
                label: 'Exaggeration',
                tip: 'Expression intensity. Default 0.5',
                val: exaggeration,
                set: setExaggeration,
                min: 0.1,
                max: 2.0,
                step: 0.1,
                marks: { 0.1: '0.1', 1.0: '1.0', 2.0: '2.0' }
              },
              {
                key: 'c',
                label: 'CFG Weight',
                tip: 'Script adherence. Default 0.8',
                val: cfgWeight,
                set: setCfgWeight,
                min: 0.0,
                max: 1.0,
                step: 0.1,
                marks: { 0.0: '0', 0.8: '0.8', 1.0: '1' }
              }
            ].map(({ key, label, tip, val, set, min, max, step, marks }) => (
              <div key={key} style={{ marginBottom: 22 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 8
                  }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 600, color: T.inkMid }}
                  >
                    {label}
                  </span>
                  <Tooltip title={tip}>
                    <Icon
                      type="question-circle"
                      style={{
                        color: T.inkLight,
                        fontSize: 11,
                        cursor: 'help'
                      }}
                    />
                  </Tooltip>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.ink,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {val}
                  </span>
                </div>
                <Slider
                  value={val}
                  onChange={set}
                  min={min}
                  max={max}
                  step={step}
                  marks={marks}
                  disabled={isGenerating}
                  trackStyle={{ background: T.blue }}
                  handleStyle={{ borderColor: T.blue }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Generating indicator */}
        {isGenerating && (
          <div
            style={{
              marginTop: 16,
              padding: '12px 16px',
              borderRadius: 8,
              background: T.blueLight,
              border: `1px solid ${T.blueBorder}`,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: T.blue,
              fontWeight: 500
            }}
          >
            <Icon type="loading" spin style={{ fontSize: 14 }} />
            {isEasyAudioMode
              ? 'Generating voiceover…'
              : 'Generating dialogue tracks…'}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 28px',
          borderTop: `1px solid ${T.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 8,
          background: T.surface
        }}
      >
        <Button
          onClick={onClose}
          disabled={isGenerating}
          style={{ fontSize: 13 }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleGenerate}
          loading={isGenerating}
          disabled={
            !globalSelectedVoice ||
            !(voiceText && voiceText.trim()) ||
            overLimit
          }
          style={{
            fontSize: 13,
            fontWeight: 600,
            minWidth: 152,
            background: T.ink,
            borderColor: T.ink
          }}
        >
          {isGenerating ? 'Generating…' : 'Generate Voiceover'}
        </Button>
      </div>
    </Modal>
  )
}

export default VoiceoverModal
